import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard, Users, Building2, Newspaper, CalendarDays, UserPlus,
  UsersRound, Image as ImageIcon, ShoppingBag, AlertTriangle, CreditCard,
  Vote, Trophy, BarChart3, MessageSquare, Megaphone, FileText, LayoutTemplate,
  Bell, Wallet, Activity, LogOut, ChevronLeft, ChevronRight, ExternalLink,
  Loader2, Radio,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Tableau de bord" },
  { to: "/admin/membres", icon: Users, label: "Membres" },
  { to: "/admin/coordinations", icon: Building2, label: "Coordinations" },
  { to: "/admin/actualites", icon: Newspaper, label: "Actualités" },
  { to: "/admin/evenements", icon: CalendarDays, label: "Événements" },
  { to: "/admin/invitations", icon: UserPlus, label: "Invitations" },
  { to: "/admin/equipe-vitrine", icon: UsersRound, label: "Équipe Vitrine" },
  { to: "/admin/medias", icon: ImageIcon, label: "Médias" },
  { to: "/admin/boutique", icon: ShoppingBag, label: "Boutique" },
  { to: "/admin/sanctions", icon: AlertTriangle, label: "Sanctions" },
  { to: "/admin/cotisations", icon: CreditCard, label: "Cotisations" },
  { to: "/admin/votes", icon: Vote, label: "Votes" },
  { to: "/admin/elections", icon: Trophy, label: "Élections" },
  { to: "/admin/sondages", icon: BarChart3, label: "Sondages" },
  { to: "/admin/messagerie", icon: MessageSquare, label: "Messagerie" },
  { to: "/admin/communication", icon: Megaphone, label: "Communication" },
  { to: "/admin/documents", icon: FileText, label: "Documents" },
  { to: "/admin/cms-vitrine", icon: LayoutTemplate, label: "CMS Vitrine" },
  { to: "/admin/notifications", icon: Bell, label: "Notifications" },
  { to: "/admin/comptabilite", icon: Wallet, label: "Comptabilité" },
  { to: "/admin/activite", icon: Activity, label: "Activité" },
  { to: "/admin/live", icon: Radio, label: "Streaming Live" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") navigate("/dashboard");
    }
  }, [loading, user, navigate]);

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1220]">
        <Loader2 size={36} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const initials = (user.name || "AU")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-[72px]" : "w-[260px]"} shrink-0 border-r border-white/5 bg-[#0F172A] flex flex-col transition-all duration-200`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <div className="h-[78px] px-4 flex items-center gap-3 border-b border-white/5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#0052CC] grid place-items-center text-[#0B1220] font-display font-bold text-sm shrink-0">
            AFB
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display text-sm tracking-wide text-white truncate">A.F.B.</div>
              <div className="text-[10px] text-slate-400 truncate">Administration</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="ml-auto p-1.5 rounded-md hover:bg-white/5 text-slate-400"
            data-testid="sidebar-toggle"
            aria-label="Réduire la sidebar"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 admin-scroll">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                  isActive
                    ? "bg-[#1E293B] text-white border-l-2 border-[#D4AF37]"
                    : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200 border-l-2 border-transparent"
                }`
              }
            >
              <item.icon size={17} className="shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer user */}
        <div className="border-t border-white/5 px-3 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0052CC] to-[#003D99] grid place-items-center text-xs font-bold text-white shrink-0">
            {initials}
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium truncate text-white">{user.name}</div>
                <div className="text-[10px] text-slate-400 truncate">Administrateur</div>
              </div>
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="p-1.5 rounded-md text-slate-400 hover:text-[#CE1126] hover:bg-white/5"
                data-testid="admin-logout"
                aria-label="Se déconnecter"
              >
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-[78px] border-b border-white/5 bg-[#0F172A]/80 backdrop-blur sticky top-0 z-10 flex items-center justify-end px-6 gap-4">
          <Link
            to="/"
            target="_blank"
            className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-2"
            data-testid="view-public-site"
          >
            <ExternalLink size={14} /> Voir le site
          </Link>
          <button className="relative p-2 rounded-md hover:bg-white/5" data-testid="notif-bell" aria-label="Notifications">
            <Bell size={18} className="text-slate-300" />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#CE1126]" />
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 px-6 lg:px-10 py-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
